import { Button } from 'antd';
import { ConfigType, Dayjs } from 'dayjs';
import { PointerEvent, useEffect, useMemo, useRef, useState } from 'react';
import { datetime } from '../../../util/library/datetime';
import Icon, { ICON_NAME } from '../../Icon';

export interface MonthPickerValue {
    month: number;
    year: number;
}

interface IProps {
    startDate?: ConfigType;
    onChange?: (value: MonthPickerValue) => void;
}

const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

export default function MonthPicker({ startDate, onChange }: IProps) {
    const [selectedDate, setSelectedDate] = useState(datetime().value);
    const monthsContainerRef = useRef<HTMLDivElement>(null);
    const dragStartXRef = useRef(0);
    const dragStartScrollLeftRef = useRef(0);
    const hasDraggedRef = useRef(false);
    const activePointerIdRef = useRef<number | null>(null);
    const currentDate = useMemo(() => datetime().startOf('month').value, []);
    const firstDate = useMemo(() => {
        const parsedStartDate = datetime(startDate).value;

        if (!startDate || !parsedStartDate.isValid()) {
            return undefined;
        }

        const normalizedStartDate = parsedStartDate.startOf('month');
        return normalizedStartDate.isAfter(currentDate) ? currentDate : normalizedStartDate;
    }, [currentDate, startDate]);

    const selectedYear = selectedDate.year();
    const selectedMonth = selectedDate.month();
    const currentYear = currentDate.year();
    const currentMonth = currentDate.month();
    const firstYear = firstDate?.year();
    const firstMonth = firstDate?.month();
    const isCurrentYear = selectedYear === currentYear;
    const isCurrentMonth = isCurrentYear && selectedMonth === currentMonth;
    const isFirstYear = selectedYear === firstYear;
    const isFirstMonth = firstDate ? selectedDate.isSame(firstDate, 'month') : false;

    useEffect(() => {
        if (firstDate && selectedDate.isBefore(firstDate, 'month')) {
            setSelectedDate(firstDate);
        }
    }, [firstDate, selectedDate]);

    useEffect(() => {
        const selectedMonthButton = monthsContainerRef.current?.querySelector(
            `[data-month="${selectedMonth}"]`,
        );

        selectedMonthButton?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
        });
    }, [selectedMonth, selectedYear]);

    const updateSelectedDate = (date: Dayjs) => {
        setSelectedDate(date);
        onChange?.({
            month: date.month() + 1,
            year: date.year(),
        });
    };

    const handlePreviousMonth = () => {
        if (isFirstMonth) return;

        updateSelectedDate(selectedDate.subtract(1, 'month'));
    };

    const handleNextMonth = () => {
        if (isCurrentMonth) return;

        updateSelectedDate(selectedDate.add(1, 'month'));
    };

    const handlePreviousYear = () => {
        if (isFirstYear) return;

        const previousDate = selectedDate.subtract(1, 'year');
        updateSelectedDate(
            firstDate && previousDate.isBefore(firstDate, 'month')
                ? firstDate
                : previousDate,
        );
    };

    const handleNextYear = () => {
        if (isCurrentYear) return;

        const nextDate = selectedDate.add(1, 'year');
        updateSelectedDate(
            nextDate.year() === currentYear && nextDate.month() > currentMonth
                ? nextDate.month(currentMonth)
                : nextDate,
        );
    };

    const handleSelectMonth = (month: number) => {
        updateSelectedDate(selectedDate.month(month));
    };

    const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
        const container = monthsContainerRef.current;
        if (!container) return;

        dragStartXRef.current = event.clientX;
        dragStartScrollLeftRef.current = container.scrollLeft;
        hasDraggedRef.current = false;
        activePointerIdRef.current = event.pointerId;
    };

    const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const container = monthsContainerRef.current;
        if (!container || activePointerIdRef.current !== event.pointerId) return;

        const distance = event.clientX - dragStartXRef.current;
        if (Math.abs(distance) > 4) {
            hasDraggedRef.current = true;
            if (!container.hasPointerCapture(event.pointerId)) {
                container.setPointerCapture(event.pointerId);
            }
        }

        container.scrollLeft = dragStartScrollLeftRef.current - distance;
    };

    const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
        const container = monthsContainerRef.current;
        if (container?.hasPointerCapture(event.pointerId)) {
            container.releasePointerCapture(event.pointerId);
        }
        activePointerIdRef.current = null;
    };

    return (
        <div className="flex min-w-0 items-center gap-2 xl:gap-5">
            <div className="min-w-[50px] shrink-0 text-[20px] font-medium">
                {selectedYear}
            </div>

            <div className='flex min-w-0 flex-1'>
                <div className='mr-2 flex shrink-0 gap-2'>
                    <Button
                        onClick={handlePreviousYear}
                        disabled={isFirstYear}
                        aria-label="Previous year"
                        className="bt-ghost !h-[36px] !w-[36px] !rounded-full border border-[--colorMain]"
                        data-icon="true"
                    >
                        <Icon icon={ICON_NAME.ARROW} size="small" className="-rotate-90 mt-[1px] mr-[1px]" />
                    </Button>
                    <Button
                        variant='text'
                        onClick={handlePreviousMonth}
                        disabled={isFirstMonth}
                        aria-label="Previous month"
                        className="bt-ghost !h-[36px] !w-[36px] !rounded-full cursor-pointer !border-0"
                        data-icon="true"
                    >
                        <Icon icon={ICON_NAME.ARROW_2} size="large" className="mr-[2px]" />
                    </Button>
                </div>

                <div
                    ref={monthsContainerRef}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                    onClickCapture={event => {
                        if (hasDraggedRef.current) {
                            event.stopPropagation();
                            hasDraggedRef.current = false;
                        }
                    }}
                    className="flex min-w-0 flex-1 touch-pan-y cursor-grab snap-x overflow-x-auto select-none [scrollbar-width:none] active:cursor-grabbing [&::-webkit-scrollbar]:hidden"
                >
                    {MONTHS.map((month, index) => ({ month, index }))
                        .filter(({ index }) => (
                            (!isFirstYear || firstMonth === undefined || index >= firstMonth)
                            && (!isCurrentYear || index <= currentMonth)
                        ))
                        .map(({ month, index }) => {
                            const isActive = selectedMonth === index;

                            return (
                                <button
                                    key={month}
                                    data-month={index}
                                    type="button"
                                    onClick={() => handleSelectMonth(index)}
                                    className={`
                                rounded-full flex h-[36px] shrink-0 snap-nearest items-center px-3 justify-center
                                text-[13px] font-[600] 
                                transition-all duration-300
                                ${isActive
                                            ? 'text-[var(--colorActive)]'
                                            : 'text-[--colorSub] hover:bg-[var(--colorHover)] hover:text-[var(--colorActive)]'
                                        }
                            `}
                                >
                                    {month}
                                </button>
                            );
                        })}
                </div>
                <div className="ml-2 flex shrink-0 gap-2">
                    <Button
                        onClick={handleNextMonth}
                        disabled={isCurrentMonth}
                        aria-label="Next month"
                        className="bt-ghost !h-[36px] !w-[36px] !rounded-full cursor-pointer !border-0"
                        data-icon="true"
                    >
                        <Icon icon={ICON_NAME.ARROW_2} size="large" className="rotate-180" />
                    </Button>
                    <Button
                        onClick={handleNextYear}
                        disabled={isCurrentYear}
                        aria-label="Next year"
                        className="bt-ghost !h-[36px] !w-[36px] !rounded-full border border-[--colorMain]"
                        data-icon="true"
                    >
                        <Icon icon={ICON_NAME.ARROW} size="small" className="rotate-90" />
                    </Button>
                </div>
            </div>
        </div>
    );
}
